import { useState, useEffect, useCallback } from 'react'
import useFetch from 'use-http'
import InfiniteScroll from 'react-infinite-scroller'
import { Box, Grid, Text, Link as ThemeLink } from 'theme-ui'
import { useSearchParams, usePathname, useRouter } from 'next/navigation'
import Link from 'next/link'

import Header from '../components/Header'
import FadeInImage from '../components/FadeInImage'
import Meta from '../components/Meta'
import { apiUrl } from '../environment'
import { useUserContext } from '../context/user'
import FullScreenLoader from '../components/FullScreenLoader'
import Filters from '../components/pages/yearbook/Filters'
import ClearButton from '../components/pages/yearbook/ClearButton'
import Empty from '../components/pages/yearbook/Empty'

function Yearbook() {
    const [page, setPage] = useState(0)
    const [results, setResults] = useState([])
    const [values, setValues] = useState([])
    const [hasMore, setHasMore] = useState(true)
    const [total, setTotal] = useState(4000)
    const router = useRouter()
    const searchParams = useSearchParams()
    const pathname = usePathname()
    const { tokens } = useUserContext()
    const { get, response, loading } = useFetch(apiUrl)

    const name = searchParams.get('name')
    const atmosphere = searchParams.getAll('atmosphere')
    const body = searchParams.getAll('body')
    const color = searchParams.getAll('color')
    const eye_color = searchParams.getAll('eye_color')
    const face = searchParams.getAll('face')
    const glasses = searchParams.getAll('glasses')
    const hat = searchParams.getAll('hat')
    const mouth = searchParams.getAll('mouth')
    const day = searchParams.get('day')
    const month = searchParams.get('month')
    const year = searchParams.get('year')
    const mine = searchParams.get('mine')

    const myTokens = tokens?.map((token) => token.token_id) ?? []
    const query = {
        name,
        atmosphere,
        body,
        color,
        eye_color,
        face,
        glasses,
        hat,
        mouth,
        day,
        month,
        year,
        token_id: mine ? myTokens : null,
    }

    const updateParams = (newDic) => {
        var params = new URLSearchParams(searchParams)
        for (const [key, value] of Object.entries(newDic)) {
            params.delete(key)

            if (Array.isArray(value)) {
                value.forEach((v) => {
                    params.append(key, v)
                })
            }

            if (typeof value === 'string' || value instanceof String) {
                params.append(key, value)
            }
        }

        const paramsString = params.toString()
        router.push(pathname + '?' + paramsString)
    }

    const initialResults = useCallback(async () => {
        const newResults = await get(
            createAPIQuery({
                params: query,
            })
        )

        if (response.ok) {
            setResults(newResults.results)
            setValues(newResults.values)
            setTotal(newResults.total)
            setPage(0)
            setHasMore(newResults.has_more)
        }
    }, [get, response, query])

    const loadNextPage = useCallback(async () => {
        const newPage = page + 1
        const newResults = await get(
            createAPIQuery({
                params: query,
                page: newPage,
            })
        )

        if (response.ok) {
            const combined = [...results, ...newResults.results]
            setResults(combined)
            setPage(newPage)
            setTotal(newResults.total)
            setHasMore(newResults.has_more)
        }
    }, [get, response, results, query])

    const onClearBirthday = () => {
        updateParams({ day: null, month: null, year: null })
    }

    const onClear = () => {
        updateParams({
            body: null,
            atmosphere: null,
            name: null,
            color: null,
            eye_color: null,
            face: null,
            glasses: null,
            mouth: null,
            day: null,
            month: null,
            year: null,
            mine: null,
        })
    }

    useEffect(() => {
        initialResults()
    }, [searchParams, tokens])

    if (loading && values.length === 0) {
        return <FullScreenLoader />
    }

    const queryEmpty = Object.values(query).every(
        (x) => x === null || x.length === 0
    )
    const selectedBirthday =
        query.day !== null || query.month !== null || query.year !== null
    const showClearButton =
        query.name !== null || selectedBirthday || !queryEmpty || mine !== null

    return (
        <Box
            sx={{
                display: 'flex',
                minHeight: '100vh',
                flexDirection: 'column',
            }}
        >
            <Meta
                title="Yearbook | TinyFaces NFT"
                description="Dive into the TinyFaces NFT Yearbook, a treasure trove of unique PFPs. Discover each character's distinct attributes, including their name, birthday, and more. Find your perfect digital art piece and take the opportunity to own a part of this exclusive NFT collection."
                keywords="NFT, Digital Art, PFP, Buy NFT, NFT Collection, NFT Community, NFT Art"
            />
            <Header />
            <Box sx={{ display: 'flex', flex: 1 }}>
                <Box
                    as="h1"
                    sx={{
                        width: 1,
                        height: 1,
                        display: 'inline-block',
                        overflow: 'hidden',
                        position: 'absolute',
                        border: 0,
                        padding: 0,
                        margin: 0,
                        clip: 'rect(1px,1px,1px,1px)',
                    }}
                >
                    Yearbook
                </Box>
                <Grid
                    gap={[4, 4, 6]}
                    columns={['auto', 'auto', '200px auto', '300px auto']}
                    sx={{ px: [3, 3, 6], pt: 5, flex: 1 }}
                >
                    <Box>
                        <Box
                            sx={{
                                borderBottomColor: 'border',
                                borderBottomWidth: 1,
                                borderBottomStyle: 'solid',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                pb: 3,
                            }}
                        >
                            <Text
                                sx={{
                                    flex: 1,
                                    fontSize: 5,
                                    lineHeight: 1.3,
                                    fontFamily: 'heading',
                                    display: 'block',
                                }}
                            >
                                Filters
                            </Text>
                            {showClearButton && (
                                <ClearButton onClear={onClear} />
                            )}
                        </Box>
                        <Filters
                            values={values}
                            setFilters={(name, values) => {
                                const newQuery = {
                                    ...query,
                                    [name]: values,
                                }
                                updateParams(newQuery)
                            }}
                            onNameChange={(name) => {
                                const newQuery = {
                                    ...query,
                                    name,
                                }
                                updateParams(newQuery)
                            }}
                            nameValue={name}
                            apiQuery={query}
                            onClearBirthday={onClearBirthday}
                            onChangeDay={(e) => {
                                const newQuery = {
                                    ...query,
                                    day: e.target.value,
                                }
                                updateParams(newQuery)
                            }}
                            onChangeMonth={(e) => {
                                const newQuery = {
                                    ...query,
                                    month: e.target.value,
                                }
                                updateParams(newQuery)
                            }}
                            onChangeYear={(e) => {
                                const newQuery = {
                                    ...query,
                                    year: e.target.value,
                                }
                                updateParams(newQuery)
                            }}
                            totalMine={tokens?.length}
                            mine={mine}
                            onChangeOnlyMine={(e) => {
                                const newQuery = {
                                    ...query,
                                    mine: e.target.checked ? 1 : null,
                                }
                                updateParams(newQuery)
                            }}
                        />
                    </Box>
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Box
                            sx={{
                                borderBottomColor: 'border',
                                borderBottomWidth: 1,
                                borderBottomStyle: 'solid',
                                mb: [5, 5, 7],
                            }}
                        >
                            <Text
                                sx={{
                                    fontSize: 5,
                                    lineHeight: 1.3,
                                    fontFamily: 'heading',
                                    display: 'block',
                                    pb: 3,
                                    textAlign: 'right',
                                }}
                            >
                                {total} TinyFaces
                            </Text>
                        </Box>
                        <Box sx={{ flex: 1, display: 'flex' }}>
                            {results.length > 0 ? (
                                <Box sx={{ width: '100%', pb: 8 }}>
                                    <InfiniteScroll
                                        pageStart={0}
                                        loadMore={loadNextPage}
                                        hasMore={hasMore}
                                        loader={
                                            <div className="loader" key={0}>
                                                Loading ...
                                            </div>
                                        }
                                    >
                                        <Grid
                                            gap={[3, 3, 4]}
                                            columns={[2, 2, 3, 3, 4, 5]}
                                            sx={{
                                                width: '100%',
                                            }}
                                        >
                                            {results.map((token) => (
                                                <Item
                                                    key={token.token_id}
                                                    token={token}
                                                />
                                            ))}
                                        </Grid>
                                    </InfiniteScroll>
                                </Box>
                            ) : (
                                <Empty />
                            )}
                        </Box>
                    </Box>
                </Grid>
            </Box>
        </Box>
    )
}

function Item({ token }) {
    return (
        <ThemeLink
            sx={{
                width: '100%',
                textDecoration: 'none',
                img: {
                    transition: 'transform .5s ease',
                },
                ':hover img': {
                    transform: 'scale(1.1)',
                },
            }}
            key={token.token_id}
            as={Link}
            href={`/art/${token.token_id}`}
        >
            <Box
                sx={{
                    width: '100%',
                    borderRadius: 2,
                    overflow: 'hidden',
                    position: 'relative',
                    background: 'backgroundBright',
                    paddingTop: '128%;',
                    WebkitTransform: 'translate3d(0,0,0)',
                    WebkitBackfaceVisibility: 'hidden',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        left: 0,
                        bottom: 0,
                    }}
                >
                    <FadeInImage
                        src={`${token.direct_url}?width=600&height=768`}
                    />
                </Box>
            </Box>
            <Text
                sx={{
                    pt: [2, 2, 3],
                    fontSize: 0,
                    textAlign: 'center',
                    display: 'block',
                    color: 'textDarkGrey',
                    textTransform: 'uppercase',
                }}
            >
                Token #{token.token_id}
            </Text>
            <Text
                sx={{
                    pt: 0,
                    pb: 2,
                    fontSize: [2, 2, 3],
                    textAlign: 'center',
                    display: 'block',
                    color: 'text',
                }}
            >
                {token.name}
            </Text>
        </ThemeLink>
    )
}

function createAPIQuery({ page = 0, limit = 20, params }) {
    const paramsCleaned = cleanupParams(params)

    const query = Object.keys(paramsCleaned)
        .map((key) => {
            const value = paramsCleaned[key]
            if (Array.isArray(value)) {
                return (
                    `${encodeURIComponent(key)}=` +
                    value.map((v) => encodeURIComponent(v)).join(',')
                )
            } else {
                return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
            }
        })
        .join('&')

    return `token?page=${page}&limit=20&${query}`
}

function cleanupParams(params) {
    let newParams = {}
    Object.keys(params).forEach((item) => {
        if (
            !(
                params[item]?.length === 0 ||
                params[item] === null ||
                params[item] === undefined ||
                params[item] === ''
            )
        ) {
            newParams[item] = params[item]
        }
    })

    return newParams
}

export default Yearbook
